import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from 'react'

type Tone = 'ghost' | 'danger'
type Size = 'sm' | 'md'

type Props = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  tone?: Tone
  size?: Size
  'aria-label': string
  children: ReactNode
}

const TONE_CLASS: Record<Tone, string> = {
  ghost:
    'text-ink-700 hover:bg-ink-100/60 disabled:opacity-50 disabled:hover:bg-transparent',
  danger:
    'text-ink-500 hover:text-flag hover:bg-flag/10 disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-ink-500',
}

const SIZE_CLASS: Record<Size, string> = {
  // ≥44px hit target (design.md §0). The glyph stays small (centered); the
  // button box is the full tap area.
  sm: 'w-11 h-11',
  md: 'w-12 h-12',
}

const IconButton = forwardRef<HTMLButtonElement, Props>(
  (
    {
      tone = 'ghost',
      size = 'md',
      type = 'button',
      className = '',
      title,
      children,
      ...rest
    },
    ref
  ) => (
    <button
      ref={ref}
      // eslint-disable-next-line react/button-has-type
      type={type}
      // Native desktop tooltip; falls back to the required aria-label so every
      // icon button is self-describing on hover without extra wiring.
      title={title ?? rest['aria-label']}
      // pointer-events-none on the glyph so the whole button box is the hit
      // target, not just the painted (stroke-only) icon.
      className={`grid place-items-center rounded-lg transition-colors [&>svg]:pointer-events-none focus:outline-none focus-visible:ring-2 focus-visible:ring-green-700/30 ${TONE_CLASS[tone]} ${SIZE_CLASS[size]} ${className}`}
      {...rest}
    >
      {children}
    </button>
  )
)

IconButton.displayName = 'IconButton'

export default IconButton
